/**
 * Library URL ingest matrix. Keep in sync with constants/librarySourceKit.js
 * classifyLibraryUrl.
 */

export type LibraryUrlClassId =
  | "empty"
  | "invalid"
  | "google"
  | "platform"
  | "direct-image"
  | "direct-file"
  | "article";

export type LibraryUrlClass = {
  id: LibraryUrlClassId;
  skip: boolean;
  message: string;
};

const VIDEO_URL_HOSTS = [
  "youtube.com",
  "youtu.be",
  "m.youtube.com",
  "music.youtube.com",
  "vimeo.com",
  "dailymotion.com",
  "tiktok.com",
  "twitch.tv",
  "loom.com",
  "wistia.com",
  "wistia.net",
  "streamable.com",
];

const AUDIO_URL_HOSTS = ["soundcloud.com", "spotify.com", "open.spotify.com"];

const GOOGLE_URL_HOSTS = [
  "docs.google.com",
  "drive.google.com",
  "slides.google.com",
  "sheets.google.com",
  "drive.usercontent.google.com",
];

function hostEndsWith(host: string, suffix: string) {
  return host === suffix || host.endsWith(`.${suffix}`);
}

export function classifyLibraryUrl(raw: string): LibraryUrlClass {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return { id: "empty", skip: false, message: "" };
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { id: "invalid", skip: true, message: "Enter a valid http(s) URL." };
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return { id: "invalid", skip: true, message: "URL must be http or https." };
  }
  const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
  const path = decodeURIComponent(parsed.pathname || "");

  if (GOOGLE_URL_HOSTS.some((h) => hostEndsWith(host, h))) {
    return {
      id: "google",
      skip: true,
      message:
        "Google Slides, Docs, and Drive links are not supported. File → Download → PDF, then upload.",
    };
  }
  if (
    VIDEO_URL_HOSTS.some((h) => hostEndsWith(host, h)) ||
    AUDIO_URL_HOSTS.some((h) => hostEndsWith(host, h))
  ) {
    return {
      id: "platform",
      skip: true,
      message:
        "YouTube, Vimeo, TikTok, Loom, Twitch, and similar links are skipped. Upload a file instead.",
    };
  }
  if (/\.(png|jpe?g|gif|webp)$/i.test(path)) {
    return {
      id: "direct-image",
      skip: false,
      message: "This image URL will be analyzed as an image (including text in the picture).",
    };
  }
  if (/\.(pdf|mp4|mov|webm|mkv|m4v|mp3|wav|m4a|aac|flac|ogg)$/i.test(path)) {
    return {
      id: "direct-file",
      skip: true,
      message:
        "Direct PDF, audio, and video URLs are not supported yet. Download the file, then upload it.",
    };
  }
  return {
    id: "article",
    skip: false,
    message: "We read the page text only. We do not scrape images or play embedded video.",
  };
}

export function mediaKindFromUrl(raw: string): "image" | "audio" | "video" | null {
  const classified = classifyLibraryUrl(raw);
  if (classified.id === "direct-image") return "image";
  if (classified.id === "platform") {
    try {
      const host = new URL(raw.trim()).hostname.replace(/^www\./, "").toLowerCase();
      if (AUDIO_URL_HOSTS.some((h) => hostEndsWith(host, h))) return "audio";
    } catch {
      return "video";
    }
    return "video";
  }
  if (classified.id === "direct-file") {
    const path = (() => {
      try {
        return decodeURIComponent(new URL(raw.trim()).pathname || "");
      } catch {
        return "";
      }
    })();
    if (/\.(mp4|mov|webm|mkv|m4v)$/i.test(path)) return "video";
    if (/\.(mp3|wav|m4a|aac|flac|ogg)$/i.test(path)) return "audio";
    if (/\.(png|jpe?g|gif|webp)$/i.test(path)) return "image";
  }
  return null;
}

export const URL_FIELD_HELP = [
  "YouTube / Vimeo / TikTok / Loom / Twitch → skipped. Upload a file instead.",
  "Google Slides / Docs / Drive → not supported. File → Download → PDF, then upload.",
  "Article or web page → page text only. We do not scrape images or play embedded video.",
  "Direct image URL (jpg / png / webp / gif) → analyzed as an image.",
  "Direct PDF or video URL → not supported yet. Upload the file.",
];

/** Claude image/PDF attach cap (AWS ~5 MB encoded). Keep in sync with constants/libraryLimits.js. */
export const DEFAULT_ANALYZE_MAX_BYTES = Math.floor(4.5 * 1024 * 1024);

export function mbLabel(bytes?: number) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  if (mb < 10) {
    const rounded = Math.round(mb * 10) / 10;
    return `${rounded} MB`;
  }
  return `${Math.round(mb)} MB`;
}

export type UploadSizeIssue = {
  level: "block" | "warn";
  message: string;
};

/** Extra line on oversize warnings — staff can always finish the catalog by hand. */
export const MANUAL_CATALOG_HINT =
  "You can always add tags, a transcript or snapshot, and a summary yourself after it saves.";

export function uploadSizeIssue(
  kind: "document" | "image" | "audio" | "video",
  size: number,
  caps: { documentMax?: number; mediaMax?: number; analyzeMax?: number }
): UploadSizeIssue | null {
  const analyzeMax = caps.analyzeMax || DEFAULT_ANALYZE_MAX_BYTES;
  const uploadMax = kind === "document" ? caps.documentMax : caps.mediaMax;
  if (uploadMax && size > uploadMax) {
    const label =
      kind === "document" ? "Documents" : kind === "image" ? "Images" : kind === "audio" ? "Audio" : "Video";
    return {
      level: "block",
      message: `${label} must be under ${mbLabel(uploadMax)}. Use a smaller file, or add a shorter export and fill tags, transcript, and summary yourself.`,
    };
  }
  if (kind === "image" && size > analyzeMax) {
    return {
      level: "warn",
      message: `This image is over ${mbLabel(analyzeMax)}. We'll save your original and the AI will read a smaller copy. ${MANUAL_CATALOG_HINT}`,
    };
  }
  if (kind === "document" && size > analyzeMax) {
    return {
      level: "warn",
      message: `This file is over ${mbLabel(analyzeMax)}. We'll save your original and catalog from extracted text. ${MANUAL_CATALOG_HINT}`,
    };
  }
  return null;
}

export function uploadKindHelp(
  kind: "document" | "image" | "audio" | "video" | null,
  caps?: { documentMax?: number; mediaMax?: number; analyzeMax?: number }
): string {
  const analyze = mbLabel(caps?.analyzeMax || DEFAULT_ANALYZE_MAX_BYTES);
  const docs = mbLabel(caps?.documentMax);
  const media = mbLabel(caps?.mediaMax);
  if (kind === "image") {
    return `Images: visual analysis, including text in the picture. Over ${analyze} we save your original and the AI reads a smaller copy. You can always type tags, transcript, and summary yourself.${media ? ` Max upload ${media}.` : ""}`;
  }
  if (kind === "document") {
    return `PDF and documents: under ${analyze} we attach the file; larger ones (up to ${docs || "the upload cap"}) are cataloged from extracted text. Your original is kept. You can always type tags, transcript, and summary yourself. A PDF exported from Google Slides is supported.`;
  }
  if (kind === "audio") {
    return `Audio: we transcribe speech, then summarize. This can take minutes.${media ? ` Max upload ${media}.` : ""}`;
  }
  if (kind === "video") {
    return `Video: spoken audio only. We do not read slides or on-screen text. Long files can stay on Processing for minutes.${media ? ` Max upload ${media}.` : ""}`;
  }
  return [
    `Image: visual analysis (including text in the image). Over ${analyze} we save the original; the AI reads a smaller copy.`,
    `PDF: attach if under ${analyze}; larger files use extracted text.`,
    "Audio: transcribed, then summarized.",
    "Video: spoken audio only — not slides or on-screen text.",
  ].join(" ");
}
